/**
 * Audit ZA — LoginPage.
 *
 * Email/Passwort-Login + Sign-Up. Auf erfolgreichem Login leitet ProtectedRoute
 * automatisch zurück.
 *
 * Dual-Modus per Toggle: Sign-In oder Sign-Up. Sign-Up erfordert Bestätigung
 * der Email-Adresse, falls Supabase-Setup das verlangt — Mail kommt von
 * Supabase-Default-Templates.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, KeyRound, LogIn, UserPlus } from 'lucide-react';

import { sendMagicLink, signIn, signUp } from '@/lib/auth';
import { cn } from '@/lib/utils';

type Mode = 'signin' | 'signup';

export default function LoginPage() {
  const navigate = useNavigate();

  // Auth-Welle Phase 1 (12.05.2026 reaktiviert): Login wieder scharf.
  // Wer schon eine Session hat, kommt direkt rein.
  useEffect(() => {
    void (async () => {
      const { data } = await (await import('@/lib/supabase')).supabase.auth.getSession();
      if (data.session) navigate('/', { replace: true });
    })();
  }, [navigate]);

  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setInfo(null);
    setSubmitting(true);
    try {
      const result = mode === 'signin' ? await signIn(email, password) : await signUp(email, password);
      if (result.error) {
        setErrorMsg(result.error);
      } else if (mode === 'signup' && !result.session) {
        // Supabase-Default: Sign-Up erzeugt Session erst nach Email-Bestätigung
        setInfo(
          `Account angelegt. Bitte prüfe dein Postfach (${email}) auf eine Bestätigungs-Mail.`
        );
      } else if (result.session) {
        // Eingeloggt — Routing zurück zur App
        navigate('/', { replace: true });
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-cockpit-base text-zinc-100">
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          {/* Logo + Branding */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="size-10 rounded bg-cockpit-accent/15 border border-cockpit-accent/40 flex items-center justify-center text-cockpit-accent text-lg font-bold">
              S
            </div>
            <div>
              <div className="text-lg font-semibold tracking-tight">SwingTrader</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                v5 · Trader-Cockpit
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-zinc-800/80 bg-zinc-900/40 p-5">
            <h1 className="text-base font-semibold text-zinc-100 mb-1 flex items-center gap-2">
              {mode === 'signin' ? (
                <>
                  <LogIn className="size-4 text-blue-300" /> Einloggen
                </>
              ) : (
                <>
                  <UserPlus className="size-4 text-blue-300" /> Account anlegen
                </>
              )}
            </h1>
            <p className="text-xs text-zinc-400 leading-snug mb-4">
              {mode === 'signin'
                ? 'Mit Email und Passwort anmelden — du landest danach im Cockpit.'
                : 'Neuer Account: Email + Passwort wählen. Bei Bedarf Bestätigungs-Mail prüfen.'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-zinc-500 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="dein@email.com"
                  className="w-full rounded-md bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm focus-ring"
                />
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wide text-zinc-500 mb-1">
                  Passwort
                </label>
                <div className="relative">
                  <KeyRound className="size-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="password"
                    required
                    minLength={6}
                    autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="mindestens 6 Zeichen"
                    className="w-full rounded-md bg-zinc-950 border border-zinc-700 pl-9 pr-3 py-2 text-sm focus-ring"
                  />
                </div>
              </div>

              {errorMsg && (
                <div className="rounded-md border border-red-900/40 bg-red-950/20 p-2 text-[11px] text-red-200">
                  {errorMsg}
                </div>
              )}
              {info && (
                <div className="rounded-md border border-emerald-900/40 bg-emerald-950/20 p-2 text-[11px] text-emerald-200">
                  {info}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className={cn(
                  'w-full inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-white focus-ring',
                  submitting ? 'bg-zinc-700 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500'
                )}
              >
                {mode === 'signin' ? (
                  <LogIn className="size-4" />
                ) : (
                  <UserPlus className="size-4" />
                )}
                {submitting
                  ? 'Bitte warten…'
                  : mode === 'signin'
                    ? 'Einloggen'
                    : 'Account anlegen'}
              </button>
            </form>

            <div className="mt-3 flex flex-col gap-2">
              {/* Magic-Link-Button — auch ohne Passwort einloggen */}
              <button
                type="button"
                onClick={async () => {
                  if (!email) {
                    setErrorMsg('Bitte oben deine E-Mail eintragen.');
                    return;
                  }
                  setSubmitting(true);
                  setErrorMsg(null);
                  setInfo(null);
                  const { error } = await sendMagicLink(email);
                  setSubmitting(false);
                  if (error) setErrorMsg(error);
                  else
                    setInfo(
                      `Magic-Link an ${email} geschickt. Klick auf den Link in der Mail — du bist sofort eingeloggt.`
                    );
                }}
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 px-3 py-2 text-xs text-zinc-200 focus-ring disabled:opacity-50"
              >
                <KeyRound className="size-3.5" />
                Login-Link per E-Mail schicken (ohne Passwort)
              </button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setMode((m) => (m === 'signin' ? 'signup' : 'signin'));
                    setErrorMsg(null);
                    setInfo(null);
                  }}
                  className="text-[11px] text-zinc-400 hover:text-zinc-200 hover:underline focus-ring"
                >
                  {mode === 'signin'
                    ? 'Noch keinen Account? Registrieren →'
                    : '← Zurück zum Login'}
                </button>
              </div>
            </div>
          </div>

          {/* Demo-Hinweis */}
          <div className="mt-4 rounded-lg border border-zinc-800/60 bg-zinc-900/20 p-3 text-[11px] text-zinc-400 leading-relaxed flex items-start gap-2">
            <Eye className="size-3.5 mt-0.5 shrink-0 text-cockpit-accent" />
            <div>
              <div className="font-semibold text-zinc-200">Demo-Version ohne Login</div>
              <div className="mt-0.5">
                Du willst ohne Account reinschauen?{' '}
                <a
                  href="/demo"
                  className="text-cockpit-accent hover:underline"
                >
                  Zur Demo
                </a>{' '}
                — nur Marktdaten, Watchlist, Strategien, KI-Briefings.
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
